import type { ModificationCountBySchemaType } from '@carboncopyco/data';
import {
  AiIcon,
  BalloonOutlineIcon,
  CodeOutlineIcon,
  FindReplaceOutlineIcon,
  type IconProps,
  LayoutOutlineIcon,
  ObjectIcon,
  SpliceOutlineIcon,
  UnClickIcon,
  VariableIcon,
} from '@carboncopyco/icons';
import {
  GroupSchemaType,
  NavigationEditSchemaType,
  RecordingTypes,
} from '@carboncopyco/types';
import type { SchemaType } from '@carboncopyco/types';
import type { ComponentType } from 'react';
import type { CategorizedData, CategoryId } from './types';

export const categorySchemasMap: Record<CategoryId, SchemaType[]> = {
  findReplace: [
    'replace_image',
    'replace_text',
    'replace_image_with_variable',
    'replace_link',
  ],
  blockInteractions: ['block_interactions'],
  variables: ['variable', 'image_variable'],
  customCode: ['snippet'],
  dataRules: ['transaction_rule'],
  edits: ['edit'],
  guidanceTooltips: ['guidance_tooltip'],
  objectEditingSettings: ['object_editing_settings'],
  aiPromptGroup: [GroupSchemaType.aiPromptGroup],
};
//
export const categoryNameMap: Record<CategoryId, string> = {
  findReplace: 'Find & Replace',
  blockInteractions: 'Block Events',
  variables: 'Variables',
  customCode: 'Custom Code',
  dataRules: 'Data Rules',
  edits: 'Spot Edits',
  guidanceTooltips: 'Guided Sandbox',
  objectEditingSettings: 'Database Editing',
  aiPromptGroup: 'AI Wizard',
};

export const categoryIconMap: Record<CategoryId, ComponentType<IconProps>> = {
  findReplace: FindReplaceOutlineIcon,
  blockInteractions: UnClickIcon,
  variables: VariableIcon,
  customCode: CodeOutlineIcon,
  dataRules: SpliceOutlineIcon,
  edits: LayoutOutlineIcon,
  guidanceTooltips: BalloonOutlineIcon,
  objectEditingSettings: ObjectIcon,
  aiPromptGroup: AiIcon,
};

export const categoryOrder: Record<CategoryId, number> = {
  findReplace: 1,
  blockInteractions: 2,
  variables: 3,
  customCode: 4,
  dataRules: 5,
  edits: 6,
  guidanceTooltips: 6,
  objectEditingSettings: 7,
  aiPromptGroup: 8,
};

export function findCategoryId(schemaType: SchemaType) {
  return Object.keys(categorySchemasMap).find((category) =>
    categorySchemasMap[category].includes(schemaType)
  );
}

const flattenCounts = (
  data: ModificationCountBySchemaType[]
): ModificationCountBySchemaType[] => {
  const flatten = (
    countsArray: ModificationCountBySchemaType[]
  ): ModificationCountBySchemaType[] => {
    let result: ModificationCountBySchemaType[] = [];
    countsArray.forEach((item) => {
      // we don't want to include block interactions settings in the count
      if (
        item.schemaType !== NavigationEditSchemaType.blockInteractionsSettings
      ) {
        result.push({
          modificationType: item.modificationType,
          schemaType: item.schemaType,
          total: item.total,
          counts: item?.counts,
        });
        if (item?.counts) {
          result = result.concat(flatten(item.counts));
        }
      }
    });
    return result;
  };

  return flatten(data);
};

export function categorizeData(
  data: ModificationCountBySchemaType[] = []
): CategorizedData[] {
  if (!data.length) {
    return [];
  }
  const formatData = flattenCounts(data);

  const accumulatedData = formatData.reduce((acc, item) => {
    const categoryId = findCategoryId(item.schemaType);

    if (!acc[categoryId]) {
      acc[categoryId] = {
        count: 0,
        schemaTypes: new Set(),
        relatedSchemas: [],
      };
    }
    acc[categoryId].count += item.total;
    acc[categoryId].schemaTypes.add(item.schemaType);
    acc[categoryId].relatedSchemas = item?.counts?.map(
      (count) => count.schemaType
    );

    return acc;
  }, {});

  return Object.keys(accumulatedData)
    .map((categoryId) => {
      const { count, schemaTypes, relatedSchemas } =
        accumulatedData[categoryId];
      return {
        categoryId,
        count,
        schemaTypes: Array.from(schemaTypes),
        sort: categoryOrder[categoryId],
        relatedSchemas: relatedSchemas,
      };
    })
    .toSorted((a, b) => a.sort - b.sort) as CategorizedData[];
}

export const getInfoIconProps = (
  categoryId: CategoryId,
  recordingType?: RecordingTypes,
  isChecked?: boolean,
  isIndeterminate?: boolean
) => {
  if (categoryId === 'edits') {
    return recordingType === RecordingTypes.overlay
      ? {
          disabled: true,
          label:
            'Visual edits cannot be imported to a demo based on a live app',
        }
      : {
          label:
            'Some visual edits (such as text edits, hide, delete, etc.) might not be imported properly, depending on the app’s structure. Please check the demo after importing.',
          widthClass: 'w-[353px]',
        };
  }
  if (categoryId === 'dataRules') {
    return {
      label:
        'Data rules can be imported, but can only be viewed and managed in the desktop app.',
      widthClass: 'w-[260px]',
    };
  }
  if (categoryId === 'findReplace' && isIndeterminate) {
    return {
      label:
        "Note that there are F&R rules created as part of AI Wizard. To remove them, you'll also need to remove the AI wizard from import as well.",
      widthClass: 'w-[297px]',
    };
  }
  if (categoryId === 'aiPromptGroup' && !isChecked) {
    return {
      label:
        "Unselecting this checkbox will impact the F&R text rules. Any rules created under the AI Wizard tool won't be imported.",
      widthClass: 'w-[297px]',
    };
  }
  return false;
};

export function sumSelectedModifications(
  data: CategorizedData[],
  recordingType: RecordingTypes
) {
  return recordingType === RecordingTypes.overlay
    ? data
        .filter(({ categoryId }) => categoryId !== 'edits')
        .reduce((sum, { count }) => sum + count, 0)
    : data.reduce((sum, { count }) => sum + count, 0);
}
